// ===========================================================================
// ReHome — Utókövetés: kurzoros (keyset) lapozás
//
// A menhelyi Utókövetés lista korlátlanul nőhet: sok örökbefogadott állat,
// állatonként sok helyzetjelentés. A lista ezért oldalanként érkezik, és
// állatonként ÖSSZESÍTVE — a jelentések teljes tartalma csak akkor jön le,
// amikor a felhasználó egy konkrét állatot nyit meg.
//
// A lapozás kurzoros: a kurzor az utolsó látott sor rendezőkulcsa
// (adoption_date, id). Nem OFFSET — az OFFSET minden oldalnál újraolvassa az
// előtte lévő sorokat, és ha közben új örökbefogadás kerül a lista elejére,
// a sorok elcsúsznak (egy sor kétszer jelenne meg, egy másik sosem).
// A szerveroldali rész indoklása: 043_followup_pagination.sql.
// ===========================================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, isSupabaseConfigured } from './supabase';
import { toCamelCase } from './hooks';

/** Hány sor jön egy oldalon. A felület görgetésre tölti a következőt. */
export const FOLLOWUP_PAGE_SIZE = 20;

/** Egy sor az utókövetési listában — állatonként egy, összesítve. */
export interface FollowupRow {
  id: string;
  name: string;
  imageUrl: string | null;
  gallery: string[] | null;
  privateGallery: string[] | null;
  species: string | null;
  breed: string | null;
  adoptionDate: string | null;
  adopterName: string | null;
  /** Beérkezett helyzetjelentés-típusok. */
  doneTypes: string[];
  /** Lejárt, de hiányzó típusok — ez a "figyelmeztetés" alapja. */
  missingTypes: string[];
  reportCount: number;
  lastUpdateId: string | null;
  lastUpdateAt: string | null;
  lastUpdateType: string | null;
  lastUpdateVerified: boolean | null;
  lastUpdateHasGps: boolean | null;
}

export interface FollowupSummary {
  totalAnimals: number;
  warningAnimals: number;
  totalReports: number;
}

const isDev = () => {
  try { return (import.meta as any).env?.DEV; } catch { return false; }
};

// ═══════════════════════════════════════════════════════════════════════════
// A lista
// ═══════════════════════════════════════════════════════════════════════════

export interface FollowupListState {
  rows: FollowupRow[];
  /** Az ELSŐ oldal töltése — ilyenkor csontváz jelenik meg. */
  loading: boolean;
  /** További oldal töltése — a meglévő lista látszik közben. */
  loadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  loadMore: () => void;
  /** Újratöltés az elejéről (pl. egy jelentés visszaigazolása után). */
  reload: () => void;
}

/**
 * Az utókövetési lista kurzoros lapozással.
 *
 * A helyesség két buktatója, amit itt kezelünk:
 *
 * 1. **Egyszerre több kérés.** Görgetésnél a `loadMore` könnyen elsül
 *    kétszer, mielőtt az első válasz megjönne — ilyenkor ugyanaz a kurzor
 *    menne el kétszer, és a lista megduplázódna. Egy folyamatban-lévő jelző
 *    zárja ki.
 *
 * 2. **Elavult válasz.** Ha a szűrő közben megváltozik, a régi kérés válasza
 *    később is megérkezhet, és beszúrná a már nem odavaló sorokat. Minden
 *    kérés kap egy sorszámot; csak az aktuális sorszámú válasz kerül be.
 *
 * A sorok azonosító szerint duplikátumszűrve épülnek be — így egy hálózati
 * újrapróbálkozás sem tud kétszer megjelenő sort okozni.
 */
export function useShelterFollowupList(
  shelterId: string | undefined,
  onlyWarnings: boolean,
  pageSize: number = FOLLOWUP_PAGE_SIZE
): FollowupListState {
  const [rows, setRows] = useState<FollowupRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inFlight = useRef(false);
  const generation = useRef(0);
  /** Az utolsó betöltött sor rendezőkulcsa — ez a kurzor. */
  const cursor = useRef<{ date: string | null; id: string } | null>(null);

  const fetchPage = useCallback(async (reset: boolean) => {
    if (!shelterId) {
      setRows([]); setLoading(false); setHasMore(false);
      return;
    }
    // A folyamatban lévő kérés CSAK a "továbbiak" hívást zárja ki. Egy
    // újraindítás (szűrőváltás, frissítés) mindig lefut: a sorszám úgyis
    // érvényteleníti a régi kérés válaszát. Ha ezt is kizárnánk, a töltés
    // közbeni szűrőváltás után a lista véglegesen üresen maradna — a régi
    // választ eldobnánk, újat pedig el sem indítottunk volna.
    if (!reset && inFlight.current) return;

    if (reset) {
      generation.current += 1;
      cursor.current = null;
      setLoading(true);
      setRows([]);
    } else {
      setLoadingMore(true);
    }

    const myGeneration = generation.current;
    inFlight.current = true;

    try {
      if (!isSupabaseConfigured()) {
        setRows([]); setHasMore(false); setError(null);
        return;
      }

      const { data, error: err } = await supabase.rpc('shelter_followup_page', {
        p_shelter_id: shelterId,
        p_only_warnings: onlyWarnings,
        p_limit: pageSize,
        p_cursor_date: cursor.current?.date ?? null,
        p_cursor_id: cursor.current?.id ?? null,
      });

      if (err) throw err;
      // Közben szűrőt vagy menhelyet váltottak — ez a válasz már nem érvényes.
      if (myGeneration !== generation.current) return;

      const page = toCamelCase<FollowupRow[]>(data || []);
      const last = page[page.length - 1];
      if (last) cursor.current = { date: last.adoptionDate ?? null, id: last.id };

      // Teli oldal = valószínűleg van még. Így nem kell külön count-lekérdezés
      // (ami épp az lenne, amit a lapozással el akarunk kerülni).
      setHasMore(page.length === pageSize);
      setRows(prev => {
        if (reset) return page;
        const seen = new Set(prev.map(r => r.id));
        return [...prev, ...page.filter(r => !seen.has(r.id))];
      });
      setError(null);
    } catch (e: any) {
      if (myGeneration !== generation.current) return;
      if (isDev()) console.warn('[useShelterFollowupList]', e.message);
      setError(e.message);
      setHasMore(false);
    } finally {
      // Csak az AKTUÁLIS kérés engedheti el a zárat és állíthatja a töltés
      // jelzőit — egy közben elavult kérés befejeződése különben szabaddá
      // nyilvánítaná a még futó újat.
      if (myGeneration === generation.current) {
        inFlight.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [shelterId, onlyWarnings, pageSize]);

  useEffect(() => { fetchPage(true); }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (!inFlight.current && hasMore) fetchPage(false);
  }, [fetchPage, hasMore]);

  const reload = useCallback(() => { fetchPage(true); }, [fetchPage]);

  return { rows, loading, loadingMore, hasMore, error, loadMore, reload };
}

// ═══════════════════════════════════════════════════════════════════════════
// Az összegzés
// ═══════════════════════════════════════════════════════════════════════════

/**
 * A három összegző szám. Külön kérés, mert a lista már nem tölti le mind a
 * sorokat — korábban éppen ezek miatt kellett letöltenie.
 */
export function useShelterFollowupSummary(shelterId: string | undefined): {
  summary: FollowupSummary | null;
  loading: boolean;
  refetch: () => void;
} {
  const [summary, setSummary] = useState<FollowupSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSummary = useCallback(async () => {
    if (!shelterId || !isSupabaseConfigured()) {
      setSummary(null); setLoading(false); return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('shelter_followup_summary', { p_shelter_id: shelterId });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      setSummary(row ? toCamelCase<FollowupSummary>(row) : null);
    } catch (e: any) {
      if (isDev()) console.warn('[useShelterFollowupSummary]', e.message);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [shelterId]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  return { summary, loading, refetch: fetchSummary };
}

// ═══════════════════════════════════════════════════════════════════════════
// Egy állat jelentései
// ═══════════════════════════════════════════════════════════════════════════

export interface AnimalFollowupUpdate {
  id: string;
  animalId: string;
  adopterId: string;
  updateType: string;
  imageUrl: string | null;
  gpsLat: number | null;
  gpsLng: number | null;
  mood: string | null;
  notes: string | null;
  verifiedByShelter: boolean | null;
  shelterNotes: string | null;
  createdAt: string;
}

/**
 * Egy állat helyzetjelentései, teljes tartalommal, kurzorosan lapozva.
 *
 * Ez az, ami az állat adatlapján és a részletek dialogban látszik. A listás
 * nézet szándékosan NEM ezt használja: ott állatonként egy összesítő sor van,
 * és ezek a — akár 1000 karakteres — szövegek nem is jelennének meg.
 */
export function useAnimalFollowupUpdates(
  animalId: string | undefined,
  pageSize: number = FOLLOWUP_PAGE_SIZE
): {
  updates: AnimalFollowupUpdate[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
  reload: () => void;
} {
  const [updates, setUpdates] = useState<AnimalFollowupUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const inFlight = useRef(false);
  const generation = useRef(0);
  const cursor = useRef<{ at: string; id: string } | null>(null);

  const fetchPage = useCallback(async (reset: boolean) => {
    if (!animalId || !isSupabaseConfigured()) {
      setUpdates([]); setLoading(false); setHasMore(false); return;
    }
    // A folyamatban lévő kérés CSAK a "továbbiak" hívást zárja ki. Egy
    // újraindítás (szűrőváltás, frissítés) mindig lefut: a sorszám úgyis
    // érvényteleníti a régi kérés válaszát. Ha ezt is kizárnánk, a töltés
    // közbeni szűrőváltás után a lista véglegesen üresen maradna — a régi
    // választ eldobnánk, újat pedig el sem indítottunk volna.
    if (!reset && inFlight.current) return;

    if (reset) {
      generation.current += 1;
      cursor.current = null;
      setLoading(true);
      setUpdates([]);
    } else {
      setLoadingMore(true);
    }

    const myGeneration = generation.current;
    inFlight.current = true;

    try {
      const { data, error } = await supabase.rpc('animal_followup_updates', {
        p_animal_id: animalId,
        p_limit: pageSize,
        p_cursor_at: cursor.current?.at ?? null,
        p_cursor_id: cursor.current?.id ?? null,
      });
      if (error) throw error;
      if (myGeneration !== generation.current) return;

      const page = toCamelCase<AnimalFollowupUpdate[]>(data || []);
      const last = page[page.length - 1];
      if (last) cursor.current = { at: last.createdAt, id: last.id };

      setHasMore(page.length === pageSize);
      setUpdates(prev => {
        if (reset) return page;
        const seen = new Set(prev.map(u => u.id));
        return [...prev, ...page.filter(u => !seen.has(u.id))];
      });
    } catch (e: any) {
      if (myGeneration !== generation.current) return;
      if (isDev()) console.warn('[useAnimalFollowupUpdates]', e.message);
      setHasMore(false);
    } finally {
      // Csak az AKTUÁLIS kérés engedheti el a zárat és állíthatja a töltés
      // jelzőit — egy közben elavult kérés befejeződése különben szabaddá
      // nyilvánítaná a még futó újat.
      if (myGeneration === generation.current) {
        inFlight.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [animalId, pageSize]);

  useEffect(() => { fetchPage(true); }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (!inFlight.current && hasMore) fetchPage(false);
  }, [fetchPage, hasMore]);

  const reload = useCallback(() => { fetchPage(true); }, [fetchPage]);

  return { updates, loading, loadingMore, hasMore, loadMore, reload };
}

// ═══════════════════════════════════════════════════════════════════════════
// Görgetésre töltés
// ═══════════════════════════════════════════════════════════════════════════

/**
 * A lista végére helyezett őrszem elem — ha láthatóvá válik, jön a következő
 * oldal.
 *
 * `IntersectionObserver`-rel, nem görgetés-eseménnyel: a görgetéskezelő
 * másodpercenként több tucatszor sülne el, és minden alkalommal elrendezést
 * kényszerítene (`offsetTop` olvasás) — pont a görgetés közben, amikor a
 * legjobban látszik az akadás. A megfigyelő ehelyett a böngészőre bízza a
 * kérdést, és csak a tényleges átlépéskor szól.
 *
 * A `rootMargin` szándékosan előretartó: a következő oldal már akkor
 * elindul, amikor az őrszem 400 px-re van a képernyő aljától, így a
 * felhasználó ritkán ér a lista végére töltés közben.
 */
export function useLoadMoreSentinel(
  onLoadMore: () => void,
  enabled: boolean
): (node: HTMLElement | null) => void {
  const callbackRef = useRef(onLoadMore);
  callbackRef.current = onLoadMore;

  const observerRef = useRef<IntersectionObserver | null>(null);

  return useCallback((node: HTMLElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node || !enabled) return;
    if (typeof IntersectionObserver === 'undefined') return; // SSR / régi böngésző

    const observer = new IntersectionObserver(
      entries => { if (entries.some(e => e.isIntersecting)) callbackRef.current(); },
      { rootMargin: '400px 0px' }
    );
    observer.observe(node);
    observerRef.current = observer;
  }, [enabled]);
}

/**
 * Egyetlen helyzetjelentés teljes tartalma azonosító alapján.
 *
 * A listás nézet csak az utolsó jelentés AZONOSÍTÓJÁT ismeri; a tartalmát
 * (szöveg, hangulat, fotó) akkor kérjük le, ha a menhely rákattint. Így a
 * lista ára nem függ attól, milyen hosszú beszámolókat írnak az örökbefogadók.
 */
export const fetchFollowupUpdate = async (updateId: string): Promise<AnimalFollowupUpdate | null> => {
  if (!updateId || !isSupabaseConfigured()) return null;
  try {
    const { data, error } = await supabase
      .from('post_adoption_updates')
      .select('*')
      .eq('id', updateId)
      .single();
    if (error) throw error;
    return data ? toCamelCase<AnimalFollowupUpdate>(data) : null;
  } catch (e: any) {
    if (isDev()) console.warn('[fetchFollowupUpdate]', e.message);
    return null;
  }
};
